import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Newspaper,
  Bookmark,
  History,
  User,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  UsersRound,
  Bell,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { trackEvent } from "@/lib/analytics";
import { useNotifications } from "@/hooks/useNotifications";

const Navigation = ({ user, onLogout }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { unreadCount } = useNotifications();

  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { path: "/news", icon: Newspaper, label: "News" },
    { path: "/bookmarks", icon: Bookmark, label: "My Posts" },
    { path: "/history", icon: History, label: "History" },
    { path: "/profile", icon: User, label: "Profile" },
  ];

  return (
    <>
      <nav className="sticky top-0 z-40 border-b border-gray-200/40 bg-[#F9F9F9]/95 backdrop-blur-md dark:border-[#2a2a2a]/40 dark:bg-[#121212]/95">
        <div className="mx-auto max-w-7xl px-2 sm:px-4">
          <div className="flex h-12 items-center justify-between sm:h-14">
            <div className="flex items-center space-x-8">
              <Link
                to="/news"
                className="flex items-center gap-1 text-xl font-bold leading-none text-gray-900 dark:text-white"
                aria-label="Zinformed home"
              >
                <img
                  src="/icon-single-light.png"
                  alt=""
                  className="-mr-[0.08em] inline-block h-[1.4em] w-auto object-contain align-middle dark:hidden"
                  aria-hidden="true"
                />
                <img
                  src="/icon-single.png"
                  alt=""
                  className="-mr-[0.08em] hidden h-[1.4em] w-auto object-contain align-middle dark:inline-block"
                  aria-hidden="true"
                />
                <span aria-hidden="true" className="translate-y-[0.01em]">
                  informed
                </span>
              </Link>

              <div className="hidden items-center gap-1 space-x-1 md:flex">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    onClick={() =>
                      trackEvent("nav_click", {
                        destination: item.path,
                        label: item.label,
                      })
                    }
                    className={`flex items-center space-x-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                      location.pathname === item.path
                        ? "bg-blue-600 text-white dark:bg-blue-500"
                        : "text-gray-500 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white"
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <div className="hidden items-center space-x-3 rounded-full bg-gray-100 px-3 py-1.5 dark:bg-[#1c1c1e] sm:flex">
                <div
                  className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold text-gray-800"
                  style={{ backgroundColor: user.avatar_color }}
                >
                  {user.name ? user.name[0].toUpperCase() : "U"}
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {user.name || user.email}
                </span>
              </div>

              <Link
                to="/notifications"
                onClick={() =>
                  trackEvent("nav_click", { destination: "/notifications" })
                }
                className="relative hidden rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white sm:block"
                aria-label="Notifications"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Link>

              <button
                onClick={() => {
                  toggleTheme();
                  trackEvent("theme_toggle", {
                    new_theme: theme === "dark" ? "light" : "dark",
                  });
                }}
                className="hidden rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white sm:block"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </button>

              <button
                onClick={() => {
                  trackEvent("logout", { source: "header" });
                  onLogout();
                }}
                data-testid="logout-btn"
                aria-label="Log out"
                className="hidden rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white sm:block"
              >
                <LogOut className="h-5 w-5" />
              </button>

              <button
                onClick={() => {
                  const next = !mobileMenuOpen;
                  setMobileMenuOpen(next);
                  trackEvent("mobile_menu", {
                    action: next ? "open" : "close",
                  });
                }}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
                className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white md:hidden"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-30 touch-pan-y overflow-y-auto overscroll-contain bg-[#F9F9F9] dark:bg-[#121212] md:hidden">
          <div className="space-y-2 p-4 pt-16">
            <div className="flex items-center space-x-3 rounded-lg bg-gray-100 p-4 dark:bg-[#1c1c1e]">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full text-lg font-bold"
                style={{ backgroundColor: user.avatar_color }}
              >
                {user.name ? user.name[0].toUpperCase() : "U"}
              </div>
              <span className="text-base font-medium text-gray-900 dark:text-white">
                {user.name || user.email}
              </span>
            </div>

            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => {
                  setMobileMenuOpen(false);
                  trackEvent("nav_click", {
                    destination: item.path,
                    label: item.label,
                    source: "mobile_menu",
                  });
                }}
                className={`flex items-center space-x-3 rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                  location.pathname === item.path
                    ? "bg-blue-600 text-white dark:bg-blue-500"
                    : "text-gray-500 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white"
                }`}
              >
                <item.icon className="h-6 w-6" />
                <span>{item.label}</span>
              </Link>
            ))}

            <Link
              to="/notifications"
              onClick={() => {
                setMobileMenuOpen(false);
                trackEvent("nav_click", {
                  destination: "/notifications",
                  source: "mobile_menu",
                });
              }}
              className={`flex items-center space-x-3 rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                location.pathname === "/notifications"
                  ? "bg-blue-600 text-white dark:bg-blue-500"
                  : "text-gray-500 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white"
              }`}
            >
              <div className="relative">
                <Bell className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </div>
              <span>Notifications</span>
            </Link>

            <button
              onClick={() => {
                toggleTheme();
                trackEvent("theme_toggle", {
                  new_theme: theme === "dark" ? "light" : "dark",
                  source: "mobile_menu",
                });
              }}
              className="flex w-full items-center space-x-3 rounded-lg px-4 py-3 text-base font-medium text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white"
            >
              {theme === "dark" ? (
                <Sun className="h-6 w-6" />
              ) : (
                <Moon className="h-6 w-6" />
              )}
              <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
            </button>

            <button
              onClick={() => {
                trackEvent("logout", { source: "mobile_menu" });
                onLogout();
                setMobileMenuOpen(false);
              }}
              className="flex w-full items-center space-x-3 rounded-lg px-4 py-3 text-base font-medium text-red-500 transition-colors hover:bg-gray-200 hover:text-red-600 dark:hover:bg-white/10"
            >
              <LogOut className="h-6 w-6" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
